/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Backdrop, Box, Button, CircularProgress, Dialog, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Theme as MaterialTheme, Paper, Popover, PopoverOrigin, Popper, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography, useTheme, } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';

import { useTranslation } from 'react-i18next';
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useState } from 'react';
import AccountBusinessLogic from '../../../businessLogic/AccountBusinessLogic';
import { IAccountGuiModel } from '../../../model/IAccountGuiModel';
import { Add, Close, Delete, RecentActors, Warehouse } from '@mui/icons-material';
import { IActorGuiModel } from '../../../model/IActorGuiModel';
import ActorBusinessLogic from '../../../businessLogic/ActorBusinessLogic';

interface IDialogProps {
    id?: string,
    type: "Customer" | "Supplier"
    organizationId: string | undefined,
    dialogOpen: boolean,
    handleDialogClose: () => void,
    existingActors: IActorGuiModel[],
    setActors: Dispatch<SetStateAction<IActorGuiModel[]>>,
}

const DialogActorSearch = (props: IDialogProps): JSX.Element => {

    const { id, type, organizationId, dialogOpen, handleDialogClose, existingActors, setActors } = props;

    const theme = useTheme();
    const { t, i18n } = useTranslation();

    const [loader, setLoader] = useState(false);

    const [freeText, setFreeText] = useState("");

    const [actorSearchList, setActorSearchList] = useState<IActorGuiModel[]>([]);
    const [actorSearchListError, setActorSearchListError] = useState<string[]>([]);

    const handleActorClick = (e: IActorGuiModel) => {
        if (type === "Supplier") {
            let index = props.existingActors.findIndex(f => f.supplierId === e.supplierId);
            if (index !== -1) {
                setActors(existingActors.filter((f) => e.supplierId !== f.supplierId));
            }
            else {
                setActors([...existingActors, e]);
            }
        }
        else {
            let index = props.existingActors.findIndex(f => f.customerId === e.customerId);
            if (index !== -1) {
                setActors(existingActors.filter((f) => e.customerId !== f.customerId));
            }
            else {
                setActors([...existingActors, e]);
            }
        }
    }

    useEffect(() => {

        let isMounted = true; //Avoid memory leaks. Async function running after dismount.

        const getActors = async () => {

            let logic = new ActorBusinessLogic();
            setLoader(true);
            const result = await logic.getActors(organizationId ? organizationId : "", type, freeText);
            setLoader(false);
            if (result.hasErrors) {
                if (isMounted) setActorSearchListError(result.errors);
            } else {
                if (isMounted) {
                    setActorSearchListError([]);
                    setActorSearchList(result.value);
                }
            }
                
        }
        getActors();

        return () => { isMounted = false };

    }, [freeText, organizationId])

    return <Dialog maxWidth="md"
        id={id}
        open={dialogOpen}
        onClose={handleDialogClose}>
        {actorSearchListError.length > 0 ? <Typography>{t("basic.errorWithAccounts") + actorSearchListError.join(", ")}</Typography> :
            <Box component={Paper} sx={{ border: 1, borderColor: 'divider' }}>
                <Stack direction="column">
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                        <Typography variant="h6">{type === "Supplier" ? t("basic.addSupplier") : t("basic.addCustomer")}</Typography>
                        {type === "Supplier" ? <Warehouse /> : <RecentActors />}
                    </Stack>
                    <Box padding={theme.spacing(3)}>
                        <TextField
                            size="small"
                            fullWidth
                            label={t("search.search")}
                            variant="outlined"
                            color="warning"
                            value={freeText}
                            onChange={(e) => setFreeText(e.target.value)}

                            id="freeTextId" />
                    </Box>
                    <Box maxHeight="15rem" overflow="auto">
                        <List dense>
                            {actorSearchList.map(e => {
                                return <ListItemButton onClick={() => handleActorClick(e)}>
                                    <ListItemText primary={e.name} />
                                    <ListItemIcon>
                                        {props.type === "Supplier" ?
                                            props.existingActors.findIndex(f => f.supplierId === e.supplierId) !== -1 ? <Delete /> : <Add /> :
                                            props.existingActors.findIndex(f => f.customerId === e.customerId) !== -1 ? <Delete /> : <Add />}
                                    </ListItemIcon>
                                </ListItemButton>
                            })}
                        </List>
                    </Box>
                </Stack>
            </Box>}
        <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loader}
        >
            <CircularProgress color="inherit" />
        </Backdrop>
    </Dialog>
}

export default DialogActorSearch;